// src/components/DriveUploader.jsx
import React, { useState, useEffect } from 'react';
import { Upload, FolderPlus, CheckCircle, AlertCircle, Loader, Key } from 'lucide-react';

const DriveUploader = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    categoria: '', // Modalidad deportiva (Fisicoculturismo, etc.)
    modalidad: '', // Categoría de edad (Junior, Senior, etc.)
    division: ''   // Peso en kg
  });
  const [audioFile, setAudioFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [authStatus, setAuthStatus] = useState({ authorized: false, checking: true });

  // Verificar estado de autorización al cargar
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth-status');
      const status = await response.json();
      setAuthStatus({ ...status, checking: false });
    } catch (error) {
      console.error('Error verificando autorización:', error);
      setAuthStatus({ authorized: false, checking: false });
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar que sea un archivo de audio
      if (file.type.startsWith('audio/')) {
        setAudioFile(file);
        setUploadStatus(null);
      } else {
        setUploadStatus({ type: 'error', message: 'Por favor selecciona un archivo de audio válido' });
      }
    }
  };

  const handleAuthorize = () => {
    window.open('/auth', '_blank');
    // Recargar estado después de un tiempo
    setTimeout(() => {
      checkAuthStatus();
    }, 3000);
  };

  // Si está verificando autorización
  if (authStatus.checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-6 text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-3 text-indigo-600" />
          <p>Verificando autorización...</p>
        </div>
      </div>
    );
  }

  // Si no está autorizado
  if (!authStatus.authorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <Key className="w-12 h-12 text-indigo-600 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-gray-800 mb-3">Autorización Requerida</h1>
            <p className="text-gray-600 mb-6">
              Para subir archivos a Google Drive, necesitas autorizar esta aplicación.
              Esto solo se hace una vez.
            </p>
            <button
              onClick={handleAuthorize}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center space-x-2"
            >
              <Key className="w-5 h-5" />
              <span>Autorizar con Google Drive</span>
            </button>
            <p className="text-xs text-gray-500 mt-4">
              Se abrirá una nueva ventana para autorizar con Google
            </p>
            <button
              onClick={checkAuthStatus}
              className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm"
            >
              Verificar autorización
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    // Validaciones
    if (!formData.nombre || !formData.categoria || !formData.modalidad || !formData.division) {
      setUploadStatus({ type: 'error', message: 'Por favor completa todos los campos' });
      return;
    }

    if (!audioFile) {
      setUploadStatus({ type: 'error', message: 'Por favor selecciona un archivo de audio' });
      return;
    }

    setIsLoading(true);
    setUploadStatus(null);

    try {
      // Crear FormData para enviar al backend
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('categoria', formData.categoria); // Modalidad deportiva
      formDataToSend.append('modalidad', formData.modalidad); // Categoría de edad
      formDataToSend.append('division', formData.division); // Peso
      formDataToSend.append('audioFile', audioFile);

      // Llamada al backend
      const response = await fetch('/api/upload-to-drive', {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus({
          type: 'success',
          message: `¡Archivo subido exitosamente! Archivo: ${result.folderName}`
        });

        // Limpiar formulario
        setFormData({ nombre: '', categoria: '', modalidad: '', division: '' });
        setAudioFile(null);
        document.getElementById('audioInput').value = '';

      } else {
        // Si requiere autorización
        if (result.needsAuth) {
          setAuthStatus({ authorized: false, checking: false });
          setUploadStatus({
            type: 'error',
            message: 'Autorización requerida. Por favor autoriza la aplicación.'
          });
        } else {
          setUploadStatus({
            type: 'error',
            message: result.error || 'Error al subir el archivo'
          });
        }
      }
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: 'Error de conexión. Inténtalo de nuevo.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Interfaz principal (autorizado)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center mb-6">
            <FolderPlus className="w-12 h-12 text-indigo-600 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-gray-800">Músicas - Coreografías FPF&PFI</h1>
            <p className="text-gray-600 text-sm mt-2">Completa la información y sube tu archivo</p>
            <div className="mt-2 text-xs text-green-600 flex items-center justify-center space-x-1">
              <CheckCircle className="w-4 h-4" />
              <span>Datos encriptados end-to-end</span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Campo Nombre */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre completo
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Tu nombre completo"
              />
            </div>

            {/* Campo Modalidad (deportes) */}
            <div>
              <label htmlFor="categoria" className="block text-sm font-medium text-gray-700 mb-1">
                Modalidad
              </label>
              <select
                id="categoria"
                name="categoria"
                value={formData.categoria}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Selecciona una modalidad</option>
                <option value="Fisicoculturismo">Fisicoculturismo</option>
                <option value="Culturismo">Culturismo Clásico</option>
                <option value="Coreográfico">Fitness Coreográfico</option>
                <option value="Physique">Games Classic Bodybuilding</option>
                <option value="Physique">Classic Physique</option>
                <option value="Wellness">Women's Physique</option>
                <option value="Parejas">Parejas</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            {/* Campo Categoría (edad) */}
            <div>
              <label htmlFor="modalidad" className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <select
                id="modalidad"
                name="modalidad"
                value={formData.modalidad}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Selecciona una categoría</option>
                <option value="Junior">Junior</option>
                <option value="Senior">Senior</option>
                <option value="Master">Master</option>
                <option value="Master2">Master 2</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            {/* Campo División (peso) */}
            <div>
              <label htmlFor="division" className="block text-sm font-medium text-gray-700 mb-1">
                División (peso en kg)
              </label>
              <input
                type="number"
                id="division"
                name="division"
                value={formData.division}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ej: 70"
                min="1"
                max="200"
              />
            </div>

            {/* Vista previa del nombre del archivo */}
            {formData.nombre && formData.categoria && formData.modalidad && formData.division && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">El archivo se guardará como:</span><br />
                  <span className="font-mono text-xs">
                    {formData.nombre}_{formData.categoria}_{formData.modalidad}_{formData.division}kg
                  </span>
                </p>
              </div>
            )}

            {/* Campo Archivo de Audio */}
            <div>
              <label htmlFor="audioInput" className="block text-sm font-medium text-gray-700 mb-1">
                Archivo de Audio
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="audioInput"
                  accept="audio/*"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent file:mr-4 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-sm file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {audioFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-medium">Archivo seleccionado:</span> {audioFile.name}
                  </div>
                )}
              </div>
            </div>

            {/* Botón de Envío */}
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Subiendo...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Subir Archivo</span>
                </>
              )}
            </button>
          </div>

          {/* Mensajes de Estado */}
          {uploadStatus && (
            <div className={`mt-4 p-3 rounded-md flex items-center space-x-2 ${
              uploadStatus.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {uploadStatus.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="text-sm">{uploadStatus.message}</span>
            </div>
          )}
        </div>

        {/* Información adicional */}
        <div className="mt-4 bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold text-gray-800 mb-2">Formatos de audio admitidos:</h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p>• MP3, WAV, M4A, OGG</p>
            <p>• Tamaño máximo: 50MB</p>
            <p>• El archivo se organizará automáticamente: <strong>Nombre_Modalidad_Categoría_Peso</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriveUploader;
